/**
 * FleetONE Report Data Contract
 * 
 * Este contrato define a estrutura padrão de dados para todos os relatórios FleetONE.
 * Preview e PDF devem consumir o MESMO FleetReportData.
 */

// =============================
// Interfaces Base
// =============================

export interface FleetReportMetadata {
  generatedAt: string;
  reportType: string;
  companyName?: string;
  period?: { from: string; to: string };
  appliedFilters?: Record<string, any>;
}

export interface FleetReportKpis {
  [key: string]: number | string;
}

export interface FleetReportAlert {
  type: string;
  severity: 'critical' | 'high' | 'warning' | 'info' | 'ok' | 'expired';
  vehicle_id?: string;
  driver_id?: string;
  message: string;
  priority?: string;
  title?: string;
}

export interface VehicleReportRow {
  vehicle_id: string;
  vehicle_label: string;
  kpis: Record<string, number>;
  alerts?: FleetReportAlert[];
}

export interface DriverReportRow {
  driver_id: string;
  driver_label: string;
  kpis: Record<string, number>;
  alerts?: FleetReportAlert[];
}

export interface ReportSection<T> {
  rows: T[];
  totals: Record<string, number>;
}

// =============================
// Contrato Principal
// =============================

export interface FleetReportData {
  summaryKpis: FleetReportKpis;
  
  byVehicle: ReportSection<VehicleReportRow>;
  
  byDriver: ReportSection<DriverReportRow>;
  
  charts?: Record<string, any[]>;
  
  alerts?: FleetReportAlert[];
  
  // Items específicos (ex: documentos para renewal)
  items?: any[];
  
  // Insights automáticos gerados pela RPC
  insights?: string[];
  
  metadata: FleetReportMetadata;
  
  // Erro retornado pela RPC (quando houver)
  error?: string;
}

// =============================
// Fallback para dados vazios
// =============================

export const EMPTY_REPORT_DATA: FleetReportData = {
  summaryKpis: {},
  byVehicle: { rows: [], totals: {} },
  byDriver: { rows: [], totals: {} },
  charts: {},
  alerts: [],
  items: [],
  metadata: {
    generatedAt: new Date().toISOString(),
    reportType: 'unknown'
  }
};

// =============================
// Seções Travadas (Editor)
// =============================

/**
 * Seções que NÃO podem ser desabilitadas no Editor de Relatórios.
 * Regra de negócio: Todo relatório FleetONE DEVE ter seções Por Veículo e Por Motorista.
 */
export const LOCKED_SECTIONS = ['byVehicle', 'byDriver'] as const;

// =============================
// Tipos Auxiliares
// =============================

export interface ReportTemplateSection {
  id: string;
  enabled: boolean;
  order: number;
  locked: boolean;
}

export interface ReportTemplateConfig {
  logo: { enabled: boolean; position: 'left' | 'center' | 'right' };
  header: { title: string; subtitle: string };
  sections: ReportTemplateSection[];
  columns: Record<string, string[]>;
  footer: { notes: string; showPagination: boolean };
  orientation: 'portrait' | 'landscape';
}

export interface FleetReportTemplate {
  id: string;
  company_id: string;
  report_type: string;
  template_config: ReportTemplateConfig;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

// =============================
// Mapper Único: RPC Result → FleetReportData
// =============================

/**
 * Mapeia resultado da RPC para FleetReportData.
 * REGRA: Usar esta função para TODOS os relatórios (proibido mapping por tipo em múltiplos lugares).
 */
export function mapRpcResultToFleetReportData(
  rpcResult: any,
  companyName?: string,
  appliedFilters?: Record<string, any>
): FleetReportData {
  // Se RPC retornou erro
  if (rpcResult?.error) {
    return {
      ...EMPTY_REPORT_DATA,
      error: rpcResult.error,
      metadata: {
        ...EMPTY_REPORT_DATA.metadata,
        generatedAt: new Date().toISOString()
      }
    };
  }

  // Se não há resultado
  if (!rpcResult) {
    return EMPTY_REPORT_DATA;
  }

  // Mapear resultado da RPC
  const data: FleetReportData = {
    summaryKpis: rpcResult.summaryKpis || {},
    
    byVehicle: {
      rows: (rpcResult.byVehicle?.rows || []).map((row: any) => ({
        vehicle_id: row.vehicle_id,
        vehicle_label: row.vehicle_label,
        kpis: row.kpis || {},
        alerts: row.alerts || []
      })),
      totals: rpcResult.byVehicle?.totals || {}
    },
    
    byDriver: {
      rows: (rpcResult.byDriver?.rows || []).map((row: any) => ({
        driver_id: row.driver_id,
        driver_label: row.driver_label,
        kpis: row.kpis || {},
        alerts: row.alerts || []
      })),
      totals: rpcResult.byDriver?.totals || {}
    },
    
    charts: rpcResult.charts || {},
    
    alerts: (rpcResult.alerts || []).map((alert: any) => ({
      type: alert.type || 'unknown',
      severity: mapSeverity(alert.severity || alert.priority),
      vehicle_id: alert.vehicle_id,
      driver_id: alert.driver_id,
      message: alert.message || alert.title || '',
      priority: alert.priority,
      title: alert.title
    })),
    
    items: rpcResult.items || [],
    
    // Mapear insights automáticos da RPC
    insights: Array.isArray(rpcResult.insights) ? rpcResult.insights : [],
    
    metadata: {
      generatedAt: rpcResult.metadata?.generatedAt || new Date().toISOString(),
      reportType: rpcResult.metadata?.reportType || 'unknown',
      companyName,
      period: rpcResult.metadata?.period,
      appliedFilters
    }
  };

  return data;
}

/**
 * Mapeia prioridade/severidade para valores padronizados
 */
function mapSeverity(value: string | undefined): FleetReportAlert['severity'] {
  const normalized = (value || '').toLowerCase();
  
  switch (normalized) {
    case 'critical':
      return 'critical';
    case 'high':
      return 'high';
    case 'medium':
    case 'warning':
      return 'warning';
    case 'low':
    case 'info':
      return 'info';
    case 'expired':
      return 'expired';
    default:
      return 'ok';
  }
}

/**
 * Sanitiza template para garantir que seções travadas estão habilitadas
 */
export function sanitizeReportTemplate(template: ReportTemplateConfig): ReportTemplateConfig {
  return {
    ...template,
    sections: template.sections.map(section => 
      LOCKED_SECTIONS.includes(section.id as any)
        ? { ...section, enabled: true, locked: true }
        : section
    )
  };
}

/**
 * Verifica se FleetReportData tem dados válidos
 */
export function hasReportData(data: FleetReportData): boolean {
  if (data.error) return false;
  
  const hasVehicles = data.byVehicle.rows.length > 0;
  const hasDrivers = data.byDriver.rows.length > 0;
  const hasKpis = Object.keys(data.summaryKpis).length > 0;
  const hasItems = (data.items?.length || 0) > 0;
  
  return hasVehicles || hasDrivers || hasKpis || hasItems;
}

/**
 * Formata KPIs para exibição
 */
export function formatKpiValue(key: string, value: number | string): string {
  if (typeof value === 'string') return value;
  
  const currencyKeys = ['cost', 'value', 'total', 'maintenance', 'fuel', 'savings'];
  const distanceKeys = ['km', 'distance'];
  
  const lowerKey = key.toLowerCase();
  
  if (currencyKeys.some(k => lowerKey.includes(k))) {
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  }
  
  if (distanceKeys.some(k => lowerKey.includes(k))) {
    return `${value.toLocaleString('pt-BR')} km`;
  }
  
  return value.toLocaleString('pt-BR');
}
